import { html, nothing } from "lit-html";
import { AirlineResultInformation, AirlineMetaInformation } from "Interfaces/airlineFilter";
import FRAAirlineFilter from "Components/fra-m-airline-filter/fra-m-airline-filter";
import { renderLazyImage } from "Components/fra-e-lazy-image/fra-e-lazy-image.template"; // eslint-disable-line
import { ACTIVE } from "Helper/cssClasses";

const alphabet = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
  "0-9",
];

/**
 * Renders halls in enabled/disabled state
 * @param {Array<Object>} halls
 * @param {Function} setState
 * @returns {TemplateResult}
 */
export const locationFilterHallsTemplate = (
  halls: Array<{ name: string; selected: boolean; disabled: boolean }>,
  setState: Function,
) => {
  // tha date has some faulty halls for example "1" and the "Z" hall should explicitly been shown even if there are no flights from it.
  const allHalls = ["A", "B", "C", "D", "E", "Z"];

  return allHalls
    .map(hall => ({
      name: hall,
      selected: halls.find(h => h.name === hall)?.selected,
      disabled: hall === "Z" || halls.find(h => h.name === hall)?.disabled,
    }))
    .map(hall => {
      return html`
        <button
          ?disabled=${hall.disabled}
          class="fra-m-airline-filter__filter fra-m-airline-filter__filter--hall ${hall.selected ? ACTIVE : ""}"
          @click=${() => {
            window.dataLayer?.push({
              event: "gaEvent",
              eventCategory: "airlineFilter",
              eventAction: "Hall switch",
              eventLabel: `Hall ${hall.selected ? "" : hall.name}`,
            });
            setState({ selectedHall: hall.selected ? "" : hall.name });
          }}
        >
          ${hall.name}
        </button>
      `;
    });
};

/**
 * Renders single alphabet entry in enabled/disabled state
 * @param {string} entry
 * @param {string} disabledClass
 * @param {FRAAirlineFilter} component
 * @returns {TemplateResult}
 */
const renderSingleAlphabetEntry = (entry: string, disabledClass: string, component: FRAAirlineFilter) => {
  return html`
    <li class="fra-m-airline-filter__alphabet-item">
      <a
        href="#${entry}"
        class="fra-m-airline-filter__alphabet-link ${disabledClass}"
        @click=${(e: Event) => component.handleAlphabetAnchorsClick(e)}
      >
        <span>${entry}</span>
      </a>
    </li>
  `;
};

/**
 * Renders alphabet row
 * @param {Array<AirlineResultInformation>} results
 * @param {FRAAirlineFilter} component
 * @returns {TemplateResult}
 */
export const locationFilterAlphabetTemplate = (
  results: Array<AirlineResultInformation>,
  component: FRAAirlineFilter,
) => {
  return html`
    <ul class="fra-m-airline-filter__alphabet-list">
      ${alphabet.map(entry => {
        // airlines starting with this letter
        const filteredResults = results.filter(result => new RegExp(`^[${entry}]`, "i").test(result.name));
        const disabledClass = filteredResults.length === 0 ? "fra-state-disabled" : "";
        return renderSingleAlphabetEntry(entry, disabledClass, component);
      })}
    </ul>
  `;
};

/**
 * Renders opening status of single result
 * @param {boolean} earlyCheckIn
 * @param {boolean} airRail
 * @param {AirlineMetaInformation} metaInformation
 * @returns {TemplateResult}
 */
const renderResultDetails = (earlyCheckIn: boolean, airRail: boolean, metaInformation: AirlineMetaInformation) => {
  const earlyCheckInLabel = metaInformation.earlyCheckInLabel;
  const airRailLabel = metaInformation.airRailLabel;

  const earlyCheckInIcon = earlyCheckIn === true ? "fra-icon-check" : "fra-icon-close";
  const earlyCheckInClass = earlyCheckIn === true ? "" : "fra-state-disabled";
  const airRailIcon = airRail === true ? "fra-icon-check" : "fra-icon-close";
  const airRailClass = airRail === true ? "" : "fra-state-disabled";

  return html`
    <p class="fra-m-airline-filter__result-details">
      <span class="fra-m-airline-filter__result-detail ${earlyCheckInClass}">
        ${earlyCheckInLabel}
        <fra-e-icon class="fra-e-icon ${earlyCheckInClass}" icon-id="${earlyCheckInIcon}"></fra-e-icon>
      </span>
      <span class="fra-m-airline-filter__result-detail ${airRailClass}">
        ${airRailLabel}
        <fra-e-icon class="fra-e-icon ${airRailClass}" icon-id="${airRailIcon}"></fra-e-icon>
      </span>
    </p>
  `;
};

/**
 * Renders single result
 * @param {AirlineResultInformation} result
 * @param {AirlineMetaInformation} metaInformation
 * @returns {TemplateResult}
 */
const renderSingleResult = (result: AirlineResultInformation, metaInformation: AirlineMetaInformation) => {
  return html`
    <div class="fra-m-airline-filter__result">
      <div class="fra-m-airline-filter__result-image">
        ${result.icon === null
          ? nothing
          : renderLazyImage({
              src: result.icon,
              alt: result.name,
              srcset: "",
              sizes: "",
              aspectRatio: "1:1",
            })}
      </div>
      <div class="fra-m-airline-filter__result-data">
        <a
          class="fra-m-airline-filter__result-link fra-e-link fra-e-link--with-icon fra-e-link--animated-right"
          href="${result.link}"
          @click=${() => {
            window.dataLayer?.push({
              event: "gaEvent",
              eventCategory: "airlineFilter",
              eventAction: "Airline click",
              eventLabel: `Airline ${result.name}`,
            });
          }}
          target="_blank"
        >
          <span class="fra-e-link__label">${result.name}</span>
          <fra-e-icon class="fra-e-icon" icon-id="fra-icon-arrow-right"></fra-e-icon>
        </a>
        <p class="fra-m-airline-filter__result-area">${result.zoneLabel}</p>
        ${renderResultDetails(result.earlyCheckIn, result.airRail, metaInformation)}
      </div>
    </div>
  `;
};

/**
 * Renders result section
 * @param {string} entry
 * @param {Array<AirlineResultInformation>} results
 * @param {AirlineMetaInformation} metaInformation
 * @returns {TemplateResult}
 */
const renderSingleAlphabetSection = (
  entry: string,
  results: Array<AirlineResultInformation>,
  metaInformation: AirlineMetaInformation,
) => {
  return html`
    <div class="fra-m-airline-filter__result-section">
      <div class="fra-m-airline-filter__result-section-identifier" id="${entry}">${entry}</div>
      <div class="fra-m-airline-filter__results">
        ${results.map(result => renderSingleResult(result, metaInformation))}
      </div>
    </div>
  `;
};

/**
 * Renders results
 * @param {Array<AirlineResultInformation>} results
 * @param {AirlineMetaInformation} metaInformation
 * @returns {TemplateResult}
 */
export const locationFilterResultsTemplate = (
  results: Array<AirlineResultInformation>,
  metaInformation: AirlineMetaInformation,
) => {
  if (!results) return nothing;

  return html`
    ${alphabet.map(entry => {
      // airlines starting with this letter
      const filteredResults = results
        .filter(result => new RegExp(`^[${entry}]`, "i").test(result.name))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      if (filteredResults.length === 0) return nothing;
      return renderSingleAlphabetSection(entry, filteredResults, metaInformation);
    })}
  `;
};
